import React from "react";
import { connect } from "react-redux";
import { RECEIVE_NOTIFICATION } from "../constants/actionTypes";

// Use the same API_ROOT logic as agent.js
const API_ROOT =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_ROOT_PRODUCTION
    : process.env.REACT_APP_API_ROOT;

class NotificationService extends React.Component {
  constructor(props) {
    super(props);
    this.pollInterval = null;
    this.lastNotificationCheck = null;
  }

  componentDidMount() {
    this.startPolling();
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentUser && !prevProps.currentUser) {
      this.startPolling();
    } else if (!this.props.currentUser && prevProps.currentUser) {
      this.stopPolling();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
  }

  startPolling = () => {
    if (!this.props.currentUser || this.pollInterval) {
      return;
    }

    console.log("Starting notification polling...");

    // Poll immediately, then every 10 seconds
    this.checkForNotifications();
    this.pollInterval = setInterval(this.checkForNotifications, 10000);
  };

  stopPolling = () => {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
      console.log("Stopped notification polling");
    }
  };

  checkForNotifications = async () => {
    if (!this.props.currentUser) return;

    try {
      const response = await fetch(
        `${API_ROOT}notifications/unread`,
        {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.success && data.notifications) {
          // Get stored notification IDs that we've already shown
          const shownNotifications = JSON.parse(
            localStorage.getItem("shownNotifications") || "[]"
          );

          // Show notifications that we haven't shown before
          data.notifications.forEach((notification) => {
            if (!shownNotifications.includes(notification.id)) {
              this.showNotification(notification);
              this.props.onNotificationReceived(notification);

              // Add to shown notifications list
              shownNotifications.push(notification.id);
            }
          });

          // Update shown notifications in localStorage (keep only last 50)
          const limitedShown = shownNotifications.slice(-50);
          localStorage.setItem(
            "shownNotifications",
            JSON.stringify(limitedShown)
          );
        }
      }
    } catch (error) {
      console.error("Failed to check for notifications:", error);
    }
  };

  markAsRead = async (notificationId) => {
    try {
      await fetch(
        `${API_ROOT}notifications/mark-read/${notificationId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Remove from shown notifications when marked as read so it can be shown again if it becomes unread
      const shownNotifications = JSON.parse(
        localStorage.getItem("shownNotifications") || "[]"
      );
      const updatedShown = shownNotifications.filter(
        (id) => id !== notificationId
      );
      localStorage.setItem("shownNotifications", JSON.stringify(updatedShown));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  showNotification = (notification) => {
    // Create and show a slide-in notification
    const notificationElement = document.createElement("div");
    notificationElement.className = "notification-popup";
    notificationElement.innerHTML = `
      <div class="notification-content">
        <div class="notification-header">
          <h5>${notification.title}</h5>
          <button class="notification-close" onclick="this.closest('.notification-popup').remove()">×</button>
        </div>
        <div class="notification-body">
          ${notification.content}
        </div>
        <div class="notification-actions">
          <button class="notification-mark-read" onclick="window.notificationService.markAsRead(${notification.id}); this.closest('.notification-popup').remove();">
            Mark as Read
          </button>
        </div>
      </div>
    `;

    // Store reference to this component for the onclick handler
    window.notificationService = this;

    // Add CSS styles if not already added
    if (!document.getElementById("notification-styles")) {
      const style = document.createElement("style");
      style.id = "notification-styles";
      style.textContent = `
        .notification-popup {
          position: fixed;
          top: 20px;
          right: 20px;
          width: 320px;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          z-index: 9999;
          animation: slideIn 0.3s ease-out;
        }
        
        .notification-content {
          padding: 16px;
        }
        
        .notification-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          border-bottom: 1px solid #eee;
          padding-bottom: 8px;
        }
        
        .notification-header h5 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .notification-close {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          color: #999;
          padding: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .notification-close:hover {
          color: #333;
        }
        
        .notification-body {
          color: #666;
          font-size: 14px;
          line-height: 1.4;
          margin-bottom: 12px;
        }
        
        .notification-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }
        
        .notification-mark-read {
          background: #007bff;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }
        
        .notification-mark-read:hover {
          background: #0056b3;
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notificationElement);

    // Auto-remove after 15 seconds
    setTimeout(() => {
      if (notificationElement.parentNode) {
        notificationElement.style.animation = "slideOut 0.3s ease-in";
        setTimeout(() => {
          if (notificationElement.parentNode) {
            notificationElement.remove();
          }
        }, 300);
      }
    }, 15000);

    // Play notification sound (optional)
    this.playNotificationSound();
  };

  playNotificationSound = () => {
    // Create a simple notification sound
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      // Silently fail if audio context is not available
    }
  };

  render() {
    // This component doesn't render anything visible
    return null;
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.common.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  onNotificationReceived: (notification) =>
    dispatch({ type: RECEIVE_NOTIFICATION, payload: notification }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NotificationService);
